/**
 * Active byte-transfer guard — Song Uploader only.
 *
 * Unload/navigation protection: phase === "uploading" only.
 * Visual notice: may persist briefly after PUT with SAFE copy (min readable time).
 * Do not delay upload/handoff; visual persistence is UI-only.
 * Do not keep ACTIVE copy or guards after the last real PUT completes.
 */

export const ACTIVE_BYTE_TRANSFER_PHASE = "uploading" as const;

/** Minimum time the page-level visual notice stays readable (ms). */
export const ACTIVE_PUT_VISUAL_MIN_MS = 6000;

/** Leave confirm — singular (default / one active PUT). */
export const ACTIVE_BYTE_TRANSFER_LEAVE_MESSAGE =
  "A song file is still uploading. Leave this page and the upload will be interrupted.";

/** Leave confirm — plural (2+ simultaneous active PUTs). */
export const ACTIVE_BYTE_TRANSFER_LEAVE_MESSAGE_PLURAL =
  "Song files are still uploading. Leave this page and the uploads will be interrupted.";

/** @deprecated Prefer activePutVisualActiveCopy(count). Singular default. */
export const ACTIVE_PUT_VISUAL_ACTIVE_COPY =
  "Uploading file — please stay on this page. Leave this page and the upload will be interrupted.";

/** @deprecated Prefer activePutVisualSafeCopy(count). Singular default. */
export const ACTIVE_PUT_VISUAL_SAFE_COPY =
  "Upload received — you're safe to leave this page. KrossKast is processing your song.";

/** @deprecated Use ACTIVE_PUT_VISUAL_ACTIVE_COPY / activePutVisualActiveCopy */
export const ACTIVE_BYTE_TRANSFER_BANNER = ACTIVE_PUT_VISUAL_ACTIVE_COPY;

export type ActivePutVisualKind = "active" | "safe";

export interface ActivePutVisualState {
  kind: ActivePutVisualKind | null;
  /** Wall-clock when the current visual episode first became active. */
  startedAtMs: number | null;
  /**
   * Files in this visual episode (batch). Drives singular/plural page copy.
   * Preserved through the SAFE hold so plural wording stays stable.
   */
  episodeFileCount: number;
}

export function isActiveByteTransferPhase(
  phase: string | null | undefined,
): boolean {
  return phase === ACTIVE_BYTE_TRANSFER_PHASE;
}

export function hasActiveByteTransfer(
  rows: ReadonlyArray<{ phase?: string | null }>,
): boolean {
  return rows.some((row) => isActiveByteTransferPhase(row.phase));
}

/** Count of rows currently mid-PUT (phase === "uploading"). */
export function countActiveByteTransfers(
  rows: ReadonlyArray<{ phase?: string | null }>,
): number {
  return rows.reduce(
    (n, row) => n + (isActiveByteTransferPhase(row.phase) ? 1 : 0),
    0,
  );
}

/**
 * Page-level ACTIVE warning — truthful only while a real PUT is active.
 * Includes the consequence of leaving (Owner-locked copy).
 */
export function activePutVisualActiveCopy(episodeFileCount: number): string {
  return episodeFileCount > 1
    ? "Uploading files — please stay on this page. Leave this page and the upload will be interrupted."
    : "Uploading file — please stay on this page. Leave this page and the upload will be interrupted.";
}

/**
 * Page-level SAFE handoff — shown after the last PUT completes.
 * Explicitly tells the user they may leave; ~6s min readable hold is UI-only.
 */
export function activePutVisualSafeCopy(episodeFileCount: number): string {
  return episodeFileCount > 1
    ? "Uploads received — you're safe to leave this page. KrossKast is processing your songs."
    : "Upload received — you're safe to leave this page. KrossKast is processing your song.";
}

/** SPA / beforeunload leave confirmation. */
export function activePutLeaveMessage(activePutCount: number): string {
  return activePutCount > 1
    ? ACTIVE_BYTE_TRANSFER_LEAVE_MESSAGE_PLURAL
    : ACTIVE_BYTE_TRANSFER_LEAVE_MESSAGE;
}

/**
 * Pure visual-notice state machine.
 * Unload guards must use hasActiveByteTransfer — never this kind alone.
 * episodeFileCountHint should reflect the batch (or active PUT count) while PUTs run.
 */
export function nextActivePutVisualState(
  prev: ActivePutVisualState,
  hasActivePut: boolean,
  nowMs: number,
  minVisibleMs: number = ACTIVE_PUT_VISUAL_MIN_MS,
  episodeFileCountHint: number = 1,
): ActivePutVisualState {
  if (hasActivePut) {
    return {
      kind: "active",
      startedAtMs: prev.startedAtMs ?? nowMs,
      episodeFileCount: Math.max(
        prev.episodeFileCount || 0,
        Math.max(1, episodeFileCountHint),
      ),
    };
  }

  if (prev.startedAtMs == null) {
    return { kind: null, startedAtMs: null, episodeFileCount: 0 };
  }

  const elapsed = nowMs - prev.startedAtMs;
  if (elapsed < minVisibleMs) {
    return {
      kind: "safe",
      startedAtMs: prev.startedAtMs,
      episodeFileCount: Math.max(prev.episodeFileCount || 1, 1),
    };
  }

  return { kind: null, startedAtMs: null, episodeFileCount: 0 };
}

/** True when an anchor click would leave the current document path via SPA routing. */
export function isInternalNavigationAnchor(
  anchor: HTMLAnchorElement,
  currentPathname: string = typeof window !== "undefined" ? window.location.pathname : "",
): boolean {
  if (anchor.target && anchor.target !== "" && anchor.target !== "_self") {
    return false;
  }
  if (anchor.hasAttribute("download")) return false;
  const href = anchor.getAttribute("href");
  if (!href || href.startsWith("#") || href.startsWith("mailto:") || href.startsWith("tel:")) {
    return false;
  }
  if (/^https?:\/\//i.test(href)) {
    try {
      const url = new URL(href);
      if (url.origin !== window.location.origin) return false;
      return url.pathname !== currentPathname;
    } catch {
      return false;
    }
  }
  // Relative SPA path
  const path = href.split("?")[0].split("#")[0];
  if (!path.startsWith("/")) return false;
  return path !== currentPathname;
}
